"use client";

import { useEffect, useRef } from "react";
import { useMotionValue, useSpring, useInView } from "motion/react";

interface AnimatedCounterProps {
  end: number;
  visible: boolean;
  suffix?: string;
  decimals?: number;
}

export function AnimatedCounter({
  end,
  visible,
  suffix = "",
  decimals = 0,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isInView && visible && !hasAnimated.current) {
      hasAnimated.current = true;
      motionValue.set(end);
    }
  }, [isInView, visible, end, motionValue]);

  useEffect(() => {
    const unsubscribe = springValue.on("change", (latest) => {
      if (ref.current) {
        const formatted = formatNumber(latest);
        ref.current.textContent = formatted + suffix;
      }
    });

    return () => unsubscribe();
  }, [springValue, suffix, decimals]);

  const formatNumber = (num: number) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    if (num >= 1000) {
      return Math.floor(num).toLocaleString();
    }
    return Math.floor(num).toString();
  };

  return <span ref={ref}>0{suffix}</span>;
}
